// eslint-disable-next-line @typescript-eslint/ban-ts-comment
import { format } from 'date-fns';
import { isValidJSON } from 'utils/isValidJSON';
import { object, string } from 'yup';
import { Data, Input } from 'clarifai-web-grpc/proto/clarifai/api/resources_pb';
import { grpcToRestInput } from 'utils/grpc/grpcToRest';
import { Metadata } from 'modules/InputViewer/components/InputInfo/Metadata';

type DataType = CF.API.Inputs.ImageInputData | CF.API.Inputs.VideoInputData | CF.API.Inputs.TextInputData | CF.API.Inputs.AudioInputData;
type EntityType = 'image' | 'video' | 'audio' | 'text';
type PathType = 'image_info' | 'video_info' | 'text_info' | 'audio_info';
type PathInfoType = Record<EntityType, PathType>;
type ValueFunctionType = (field: string) => string | number | JSX.Element;
export type FieldType = { label: string; value: string | number | JSX.Element };

/**
 * Takes created date and returns formatted version
 */
export const formatDate = (created_at: string, expectedFormat = 'MMM dd, yyyy, h:mm:ss a'): string => format(new Date(created_at), expectedFormat);

/**
 * Takes metadataString and returns JSX Element
 */
const metadataCode = (input: CF.API.Inputs.Input): JSX.Element => {
  const metadataString = JSON.stringify(input.data.metadata, null, '  ');
  return <Metadata input={input} metadataString={metadataString} />;
};

/**
 * Takes input and returns JSX Element
 */
const getCharsCount = (input: CF.API.Inputs.TextInput): JSX.Element => {
  const charCount = input?.data?.text?.text_info?.char_count;

  return <span> {charCount ? charCount.toLocaleString('en-US') : null}</span>;
};

const labels = {
  inputId: 'Input ID',
  createdDate: 'Created Date',
  resolution: 'Resolution',
  inputFormat: 'Format',
  colorMode: 'Color Mode',
  metadata: 'Metadata',
  fps: 'FPS',
  encoding: 'Encoding',
  charsCount: 'Chars Count',
};

const PATH_INFO: PathInfoType = {
  image: 'image_info',
  video: 'video_info',
  text: 'text_info',
  audio: 'audio_info',
};

/**
 * Takes data and returns type
 */
const getTypeofData = (data: DataType): EntityType => {
  if ('image' in data) return 'image' as const;
  if ('video' in data) return 'video' as const;
  if ('text' in data) return 'text' as const;
  return 'audio' as const;
};

/**
 * Takes data and returns resolution
 */
const getResolution = (data: DataType): string => {
  const typeofData = getTypeofData(data) as keyof DataType;

  const dataInfo = PATH_INFO[typeofData as EntityType] as PathType;
  return typeofData in data && typeofData === 'metadata' ? `${data[typeofData]?.[dataInfo].width}x${data[typeofData]?.[dataInfo].height}` : '';
};

/**
 * Takes data and returns format
 */
const getFormat = (data: DataType): string => {
  const typeofData = getTypeofData(data) as keyof DataType;
  const dataInfo = PATH_INFO[typeofData as EntityType] as PathType;
  const formatMap = {
    image: 'format',
    video: 'video_format',
  };
  const formatInfo = formatMap[typeofData as 'image'];
  return typeofData === 'metadata' ? data[typeofData]?.[dataInfo][formatInfo] : '';
};

/**
 * Takes data and returns color mode
 */
const getColorMode = (data: DataType): string => {
  const typeofData = getTypeofData(data) as keyof DataType;
  const dataInfo = PATH_INFO[typeofData as EntityType] as PathType;
  return typeofData === 'metadata' ? data[typeofData]?.[dataInfo].color_mode : '';
};

/**
 * Takes data and returns FPS
 */
const getFps = (data: DataType): string => {
  const typeofData = getTypeofData(data) as keyof DataType;
  const dataInfo = PATH_INFO[typeofData as EntityType] as PathType;
  return typeofData === 'metadata' ? data[typeofData]?.[dataInfo].fps : '';
};

/**
 * Takes data and returns encoding
 */
const getEncoding = (data: DataType): string => {
  const typeofData = getTypeofData(data) as keyof DataType;
  const dataInfo = PATH_INFO[typeofData as EntityType] as PathType;
  return typeofData === 'metadata' ? data[typeofData]?.[dataInfo].encoding : '';
};

/**
 * Takes values and field
 * Based on the field, returns data
 */
const getValue =
  (values: CF.API.Inputs.Input): ValueFunctionType =>
  (field: string): string | number | JSX.Element => {
    const { id, created_at = new Date().toISOString(), data } = values;
    switch (field) {
      case labels.inputId:
        return id;
      case labels.createdDate:
        return formatDate(created_at);
      case labels.resolution:
        return getResolution(data);
      case labels.inputFormat:
        return getFormat(data);
      case labels.colorMode:
        return getColorMode(data);
      case labels.fps:
        return getFps(data);
      case labels.encoding:
        return getEncoding(data);
      case labels.metadata:
        return metadataCode(values);
      case labels.charsCount:
        return getCharsCount(values as CF.API.Inputs.TextInput);
      default:
        return '';
    }
  };

/**
 * Takes input object
 * Returns { label, value } array
 */
const getLabelValuePairs =
  (values: CF.API.Inputs.Input) =>
  (...args: string[]) => {
    return args.map((arg) => {
      return { label: arg, value: getValue(values)(arg) };
    });
  };

/**
 * Takes input and metadataString
 * Based on the input type, creates a fields array and returns it
 */
export const getFields = (input: Input): FieldType[] => {
  const data = input?.getData();
  const { inputId, createdDate, resolution, inputFormat, colorMode, fps, encoding, metadata, charsCount } = labels;
  const getFormattedValues = getLabelValuePairs(grpcToRestInput(input));

  if (data?.hasImage() && data?.getImage()?.getImageInfo()) {
    return getFormattedValues(inputId, createdDate, resolution, inputFormat, colorMode, metadata);
  } else if (data?.hasVideo() && data?.getVideo()?.getVideoInfo()) {
    return getFormattedValues(inputId, createdDate, resolution, inputFormat, fps, metadata);
  } else if (data?.hasText() && data?.getText()?.getTextInfo()) {
    return getFormattedValues(inputId, charsCount, createdDate, encoding, metadata);
  }

  return getFormattedValues(inputId, createdDate, metadata);
};

/**
 * Checks if JSON is valid or not
 */
export const validationSchema = object().shape({
  metadata: string().test('is-valid-json', 'Not a valid JSON object', (text) => text == null || isValidJSON(text, true)),
});

export const getInputTypeFromData = (inputData: Data | undefined): CF.UI.UIMediaTypes | undefined => {
  if (inputData?.hasImage()) return 'image';
  if (inputData?.hasAudio()) return 'audio';
  if (inputData?.hasVideo()) return 'video';
  if (inputData?.hasText()) return 'text';
  return undefined;
};
